import React, { useState, useRef, useEffect } from 'react'
import { X, Camera, Mail, Shield, Briefcase, Home, Trash2, Upload, Save, UserCheck, UserX, Wifi, WifiOff } from 'react-feather'
import { useAuth } from '../../Context/AuthContext'
import axios from 'axios'
import baseUrl from '../../Utils/baseUrl'

interface TeamMemberPanelProps {
  isOpen: boolean
  onClose: () => void
  member: any
}

const TeamMemberPanel: React.FC<TeamMemberPanelProps> = ({ isOpen, onClose, member }) => {
  const { currentProfile, grabAllProfiles } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState('')
  const [department, setDepartment] = useState('')
  const [privileges, setPrivileges] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [remoteAccess, setRemoteAccess] = useState(false)
  const [profilePhoto, setProfilePhoto] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [showPhotoMenu, setShowPhotoMenu] = useState(false)
  const [memberId, setMemberId] = useState<string | null>(null)
  const prevMemberIdRef = React.useRef<string | null>(null)

  // Store original values to compare against (not affected by photo refresh)
  const originalValuesRef = React.useRef<{name: string, department: string, privileges: string, isActive: boolean, remoteAccess: boolean} | null>(null)
  const formInitializedRef = React.useRef(false)
  const userIdRef = React.useRef<string | null>(null)

  // Track member ID to prevent panel from closing during photo upload
  useEffect(() => {
    if (member?.userid) {
      setMemberId(member.userid)
      userIdRef.current = member.userid
    }
  }, [member?.userid])

  // Clear member ID when panel closes
  useEffect(() => {
    if (!isOpen) {
      setMemberId(null)
      prevMemberIdRef.current = null
      originalValuesRef.current = null
      formInitializedRef.current = false
      userIdRef.current = null
    }
  }, [isOpen])

  const departmentOptions = ['intake', 'administration', 'dev', 'claims', 'followup', 'bd']
  const allPrivilegesOptions = ['staff', 'manager', 'director', 'admin', 'dev', 'owner']
  // Managers can only assign staff or manager roles, directors can also assign director
  const privilegesOptions = currentProfile.privileges === 'manager'
    ? ['staff', 'manager']
    : currentProfile.privileges === 'director'
      ? ['staff', 'manager', 'director']
      : allPrivilegesOptions

  // Managers and directors can edit their team, admin/dev/owner can edit all. Staff can only view.
  const canEdit = ['admin', 'dev', 'owner', 'manager', 'director'].includes(currentProfile.privileges || '')
  const canEditPrivileges = ['dev', 'owner', 'manager', 'director'].includes(currentProfile.privileges || '')
  const canEditDepartment = ['dev', 'owner'].includes(currentProfile.privileges || '')
  const canToggleActive = currentProfile.privileges === 'dev'
  const canEditRemoteAccess = ['dev', 'owner', 'admin'].includes(currentProfile.privileges || '')

  // Update state when opening panel for a member - only runs once per member
  useEffect(() => {
    if (member && isOpen) {
      // Only initialize form once per panel open for this specific member
      const isNewMember = prevMemberIdRef.current !== member.userid
      const shouldInitialize = !formInitializedRef.current || isNewMember

      if (shouldInitialize) {
        // New member selected or first load - initialize form
        setName(member.name || '')
        setDepartment(member.department || '')
        setPrivileges(member.privileges || '')
        setIsActive(member.active !== false)
        setRemoteAccess(member.remote_access === true)
        setProfilePhoto(member.profile_photo || '')
        setHasChanges(false)
        prevMemberIdRef.current = member.userid
        formInitializedRef.current = true
        // Store original values for change detection
        originalValuesRef.current = {
          name: member.name || '',
          department: member.department || '',
          privileges: member.privileges || '',
          isActive: member.active !== false,
          remoteAccess: member.remote_access === true
        }
      }
      // Don't update anything if form is already initialized for this member
    }
  }, [member?.userid, isOpen]) // Only depend on member ID and isOpen, not the full member object

  // Track changes - compare against original values (not affected by photo refresh)
  useEffect(() => {
    if (!originalValuesRef.current) return
    const orig = originalValuesRef.current
    const nameChanged = name !== orig.name
    const deptChanged = department !== orig.department
    const privChanged = privileges !== orig.privileges
    const activeChanged = isActive !== orig.isActive
    const remoteChanged = remoteAccess !== orig.remoteAccess
    setHasChanges(nameChanged || deptChanged || privChanged || activeChanged || remoteChanged)
  }, [name, department, privileges, isActive, remoteAccess])

  // Keep panel open if we have a tracked member ID (prevents closing during photo refresh)
  if (!isOpen || (!member && !memberId)) return null

  const handlePhotoClick = () => {
    if (canEdit) {
      setShowPhotoMenu(!showPhotoMenu)
    }
  }

  const handleUploadClick = () => {
    setShowPhotoMenu(false)
    fileInputRef.current?.click()
  }

  const handleRemovePhoto = async () => {
    const userId = member?.userid || memberId || userIdRef.current
    if (!profilePhoto || !canEdit || !userId) return

    setShowPhotoMenu(false)
    setIsRemoving(true)

    try {
      const response = await axios.put(
        `${baseUrl}/users/${userId}/profile`,
        { profile_photo: null }
      )

      if (response.data.status === 'Success') {
        setProfilePhoto('')
        // Don't call grabAllProfiles() here - it resets unsaved changes
      }
    } catch (error) {
      console.error('Failed to remove photo:', error)
      alert('Failed to remove photo. Please try again.')
    } finally {
      setIsRemoving(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const userId = member?.userid || memberId || userIdRef.current
    if (!file || !canEdit || !userId) return

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB')
      return
    }

    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await axios.post(
        `${baseUrl}/users/${userId}/profile-photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )

      if (response.data.status === 'Success') {
        setProfilePhoto(response.data.profile_photo_url)
        // Don't call grabAllProfiles() here - it resets unsaved changes
        // Profiles will refresh after save or when panel closes
      }
    } catch (error) {
      console.error('Failed to upload photo:', error)
      alert('Failed to upload photo. Please try again.')
    } finally {
      setIsUploading(false)
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleSave = async () => {
    const userId = member?.userid || memberId || userIdRef.current
    if (!hasChanges || !canEdit || !userId) return

    setIsSaving(true)

    try {
      const updateData: any = { name }

      if (canEditDepartment) {
        updateData.department = department
      }

      if (canEditPrivileges) {
        updateData.privileges = privileges
      }

      if (canToggleActive) {
        updateData.active = isActive
      }

      if (canEditRemoteAccess) {
        updateData.remote_access = remoteAccess
      }

      const response = await axios.put(
        `${baseUrl}/users/${userId}/profile`,
        updateData
      )

      if (response.data.status === 'Success') {
        // Update original values to match saved values
        originalValuesRef.current = { name, department, privileges, isActive, remoteAccess }
        grabAllProfiles()
        setHasChanges(false)
      }
    } catch (error) {
      console.error('Failed to update profile:', error)
      alert('Failed to update profile. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const getInitials = (name: string | null) => {
    if (!name) return 'U'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'from-amber-500/20 to-yellow-500/20 text-amber-400'
      case 'admin': return 'from-purple-500/20 to-purple-500/20 text-purple-400'
      case 'dev': return 'from-emerald-500/20 to-emerald-500/20 text-emerald-400'
      case 'director': return 'from-rose-500/20 to-rose-500/20 text-rose-400'
      case 'manager': return 'from-blue-500/20 to-blue-500/20 text-blue-400'
      case 'staff': return 'from-slate-500/20 to-slate-500/20 text-slate-400'
      default: return 'from-slate-500/20 to-slate-500/20 text-slate-400'
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-dark-card border-l border-dark-border shadow-2xl z-50 overflow-y-auto animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="relative h-28 bg-gradient-to-r from-accent-teal via-accent-purple to-accent-deep-purple">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg bg-black/20 hover:bg-black/40 transition-colors"
          >
            <X size={20} className="text-white" />
          </button>
          <div className="absolute bottom-4 left-6">
            <h2 className="text-white font-semibold text-lg">Team Member</h2>
            <p className="text-white/70 text-sm">View and edit details</p>
          </div>
        </div>

        {/* Profile Photo */}
        <div className="relative -mt-12 flex justify-center">
          <div className="relative">
            <div
              onClick={handlePhotoClick}
              className={`relative group ${canEdit ? 'cursor-pointer' : ''}`}
            >
              {profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt="Profile"
                  className="w-24 h-24 rounded-full border-4 border-dark-card object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full border-4 border-dark-card bg-gradient-to-br from-accent-teal to-accent-purple flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {getInitials(member.name)}
                  </span>
                </div>
              )}
              {canEdit && (
                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  {(isUploading || isRemoving) ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera size={24} className="text-white" />
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* Photo Options Menu */}
            {showPhotoMenu && canEdit && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowPhotoMenu(false)}
                />
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-dark-card border border-dark-border rounded-xl shadow-xl overflow-hidden z-20 w-44">
                  <button
                    onClick={handleUploadClick}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-white hover:bg-dark-card-hover transition-colors"
                  >
                    <Upload size={16} className="text-accent-teal" />
                    Upload Photo
                  </button>
                  {profilePhoto && (
                    <button
                      onClick={handleRemovePhoto}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors border-t border-dark-border"
                    >
                      <Trash2 size={16} />
                      Remove Photo
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 pt-4">
          {/* Name */}
          <div className="text-center mb-6">
            {canEdit ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="text-xl font-bold text-white bg-transparent border-b-2 border-transparent hover:border-dark-border focus:border-accent-teal px-2 py-1 text-center w-full focus:outline-none transition-colors"
                placeholder="Name"
              />
            ) : (
              <h3 className="text-xl font-bold text-white">{member.name}</h3>
            )}
            <p className="text-sm text-gray-400 mt-1">{member.email}</p>
          </div>

          {/* Info Cards */}
          <div className="space-y-3">
            {/* Email (read-only) */}
            <div className="flex items-center gap-3 p-3 bg-dark-input rounded-xl">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-teal/20">
                <Mail size={18} className="text-accent-teal" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm text-white truncate">{member.email}</p>
              </div>
            </div>

            {/* Department */}
            <div className="flex items-center gap-3 p-3 bg-dark-input rounded-xl">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-purple/20">
                <Briefcase size={18} className="text-accent-purple" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Department</p>
                {canEditDepartment ? (
                  <select
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="w-full text-sm text-white bg-transparent border-none focus:outline-none cursor-pointer capitalize"
                  >
                    {departmentOptions.map((dept) => (
                      <option key={dept} value={dept} className="bg-dark-card capitalize">{dept}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-white capitalize">{member.department}</p>
                )}
              </div>
            </div>

            {/* Role/Privileges */}
            <div className="flex items-center gap-3 p-3 bg-dark-input rounded-xl">
              <div className={`flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${getRoleColor(privileges)}`}>
                <Shield size={18} />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Role</p>
                {canEditPrivileges ? (
                  <select
                    value={privileges}
                    onChange={(e) => setPrivileges(e.target.value)}
                    className="w-full text-sm text-white bg-transparent border-none focus:outline-none cursor-pointer capitalize"
                  >
                    {privilegesOptions.map((priv) => (
                      <option key={priv} value={priv} className="bg-dark-card capitalize">{priv}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-white capitalize">{member.privileges}</p>
                )}
              </div>
            </div>

            {/* Company (read-only) */}
            {member.company && (
              <div className="flex items-center gap-3 p-3 bg-dark-input rounded-xl">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-orange/20">
                  <Home size={18} className="text-accent-orange" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Company</p>
                  <p className="text-sm text-white">{member.company}</p>
                </div>
              </div>
            )}

            {/* Status Toggle (dev only) */}
            {canToggleActive && (
              <div className="flex items-center gap-3 p-3 bg-dark-input rounded-xl">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${isActive ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  {isActive ? (
                    <UserCheck size={18} className="text-green-400" />
                  ) : (
                    <UserX size={18} className="text-red-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Status</p>
                  <p className={`text-sm font-medium ${isActive ? 'text-green-400' : 'text-red-400'}`}>
                    {isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <button
                  onClick={() => setIsActive(!isActive)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${isActive ? 'left-7' : 'left-1'}`} />
                </button>
              </div>
            )}

            {/* Remote Access Toggle */}
            {canEditRemoteAccess && (
              <div className="flex items-center gap-3 p-3 bg-dark-input rounded-xl">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${remoteAccess ? 'bg-cyan-500/20' : 'bg-gray-500/20'}`}>
                  {remoteAccess ? (
                    <Wifi size={18} className="text-cyan-400" />
                  ) : (
                    <WifiOff size={18} className="text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Remote Access</p>
                  <p className={`text-sm font-medium ${remoteAccess ? 'text-cyan-400' : 'text-gray-400'}`}>
                    {remoteAccess ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <button
                  onClick={() => setRemoteAccess(!remoteAccess)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${remoteAccess ? 'bg-cyan-500' : 'bg-gray-600'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${remoteAccess ? 'left-7' : 'left-1'}`} />
                </button>
              </div>
            )}
          </div>

          {/* Save Button */}
          {canEdit && (
            <div className="mt-6">
              <button
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
                  hasChanges
                    ? 'bg-gradient-to-r from-accent-teal to-accent-purple text-white hover:opacity-90'
                    : 'bg-dark-input text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSaving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save size={18} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}

          {/* User ID (for reference) */}
          <div className="mt-6 pt-4 border-t border-dark-border">
            <p className="text-xs text-gray-500 text-center">
              User ID: <span className="text-gray-400 font-mono">{member.userid}</span>
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default TeamMemberPanel
